<svelte:options customElement="hb-input-select" />

<script lang="ts">
  /**
   * Svelte Input Select WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import { untrack } from "svelte";
  import type { Component } from "./types/webcomponent.type";
  import {
    parseSchemaentryProp,
    sameSetValPayload,
    schemaentryFingerprint,
    shouldApplySchemaSync,
  } from "../lib/schemaentry";

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }
  let {
    schemaentry = undefined,
    show_validation = "no",
    is_small = "no",
  }: Component = $props();

  let field = $state<Record<string, any> | null>(null);
  let schemaFingerprint = $state("");

  let options: { value: string; label?: string; id: string }[] = $derived(
    field?.params?.options ?? [],
  );
  let valid = $derived.by(
    () => !field?.required || (value != null && value !== ""),
  );

  let value: string | undefined = $state(undefined);

  let lastSetVal = $state<{
    value: unknown;
    valid: boolean;
    id: string | undefined;
  } | null>(null);

  $effect(() => {
    const fp = schemaentryFingerprint(schemaentry);
    const nextField = parseSchemaentryProp(schemaentry);
    if (!nextField) {
      field = null;
      return;
    }
    if (shouldApplySchemaSync(fp, () => schemaFingerprint)) {
      const hadPriorSchema = untrack(() => schemaFingerprint !== "");
      schemaFingerprint = fp;
      field = nextField;
      const v = nextField.value;
      value =
        v !== undefined && v !== null ? String(v) : undefined;
      // After first mount, aligning from parent schema must not emit `setVal` (consumers
      // like hb-paginate would treat it as user input → changePageSize → page reset).
      if (hadPriorSchema) {
        const vStr = value;
        const nextValid =
          !nextField?.required || (vStr != null && vStr !== "");
        lastSetVal = { value: vStr, valid: nextValid, id: nextField.id };
      }
    }
  });

  $effect(() => {
    const f = field;
    if (!f?.id) return;
    const payload = { value, valid, id: f.id as string | undefined };
    if (sameSetValPayload(lastSetVal, payload)) return;
    lastSetVal = payload;
    dispatch("setVal", { value, valid, id: f.id });
  });
</script>

{#if field != null}
  <div class="field">
    <div class="control">
      <div
        class="select"
        class:is-small={is_small === "yes"}
        class:is-success={show_validation === "yes" &&
          !!field?.required &&
          valid}
        class:is-danger={show_validation === "yes" &&
          !!field?.required &&
          !valid}
      >
        <select
          bind:value
          id={field?.id}
          required={field?.required}
          disabled={!!field?.readonly || !!field?.disabled}
        >
          {#each options as option (option.id)}
            <option value={option.value} selected={value === option.value}
              >{option.label ?? option.value}</option
            >
          {/each}
        </select>
      </div>
    </div>

    {#if field?.validationTip && show_validation === "yes" && !valid}
      <p part="invalid-feedback" class="help is-danger">
        {field.validationTip}
      </p>
    {/if}
  </div>
{/if}

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
